import * as Sentry from '@sentry/react';

// ---------------------------------------------------------------------------
// Browser error monitoring.
//
// The backend has reported its own errors since the Sentry step; this is the
// other half. A React render error currently produces a WHITE SCREEN — no
// message, nothing in any log we keep, and the only signal is a seller
// deciding whether to tell us. This reports it, and shows them something
// better than a blank page.
//
// OFF UNLESS REACT_APP_SENTRY_DSN IS SET, which is how `npm start` and the CI
// build stay silent. CRA inlines REACT_APP_* at BUILD time, so this is decided
// when the bundle is produced, not when it runs.
//
// WHAT IS DELIBERATELY NOT SENT. A browser event can carry far more than a
// server one, and everything on these screens belongs to a seller or their
// buyer:
//
//   · session replay is OFF. It would record the screen — a label PDF has
//     every buyer's name, address and phone on it. Sentry offers it and it is
//     the single worst thing we could switch on.
//   · no request bodies, and the Authorization header is scrubbed: it is a
//     working session token, and a stolen one is a logged-in seller.
//   · URLs keep their path but lose query values that look like tokens.
//   · localStorage is never attached. It holds the JWT.
//
// Only the seller's id identifies them — never the email, because an error
// tracker is not a customer list.
// ---------------------------------------------------------------------------

const SENSITIVE = /(pass|secret|token|key|auth|cookie|session|otp|dsn|email|phone|address)/i;

/** Redacts anything that looks sensitive, at any depth. */
export function scrub(value, depth = 0) {
  if (!value || typeof value !== 'object' || depth > 4) return value;
  for (const [k, v] of Object.entries(value)) {
    if (SENSITIVE.test(k)) value[k] = '[redacted]';
    else if (v && typeof v === 'object') scrub(v, depth + 1);
  }
  return value;
}

let enabled = false;

/** True once monitoring is running. */
export const isEnabled = () => enabled;

export function init() {
  const dsn = process.env.REACT_APP_SENTRY_DSN;
  if (!dsn) return false;

  Sentry.init({
    dsn,
    environment: process.env.REACT_APP_ENVIRONMENT || 'production',
    // Errors are always reported; traces are sampled, because this is a free
    // Sentry plan and a busy afternoon should not spend the quota on timings.
    tracesSampleRate: 0.1,
    // No replay integration, on purpose — see the note above.
    integrations: [],
    sendDefaultPii: false,

    // Noise that is not ours and cannot be acted on: a browser extension
    // throwing inside our page, or a network drop mid-request.
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      'Non-Error promise rejection captured',
      /^Network Error$/,
      /Loading chunk \d+ failed/
    ],
    denyUrls: [/extensions\//i, /^chrome:\/\//i, /^moz-extension:\/\//i],

    beforeSend(event) {
      if (event.request) {
        delete event.request.data;
        delete event.request.cookies;
        if (event.request.headers) scrub(event.request.headers);
        // Keep the path, drop the values: "?token=abc" says nothing useful
        // and carries something that matters.
        if (typeof event.request.url === 'string' && event.request.url.includes('?')) {
          const [path, query] = event.request.url.split('?');
          const kept = query.split('&')
            .map((pair) => {
              const [k] = pair.split('=');
              return SENSITIVE.test(k) ? `${k}=[redacted]` : pair;
            })
            .join('&');
          event.request.url = `${path}?${kept}`;
        }
      }
      if (event.extra) scrub(event.extra);
      if (event.contexts) scrub(event.contexts);
      return event;
    },

    beforeBreadcrumb(breadcrumb) {
      // A console breadcrumb can contain anything a developer logged, and a
      // fetch breadcrumb can contain a request body. Both are dropped.
      if (breadcrumb.category === 'console') return null;
      if (breadcrumb.data) scrub(breadcrumb.data);
      return breadcrumb;
    }
  });

  enabled = true;
  return true;
}

/** Notes which account hit the error. The id only. */
export function identify(user) {
  if (!enabled) return;
  if (!user?.id) { Sentry.setUser(null); return; }
  Sentry.setUser({ id: String(user.id) });
}

/** Reports an error from a path that catches and carries on. */
export function capture(error, context) {
  if (!enabled) return;
  Sentry.captureException(error, context ? { extra: scrub({ ...context }) } : undefined);
}

export { Sentry };
